/** Vocabulary for the /audit activity feed.
 *
 *  All per-kind copy lives here so the Work tab, the Security tab and the
 *  per-user drill-down phrase the same event identically. The backend
 *  (`app/services/activity_feed.py`) deliberately returns flat data and no
 *  prose — sentences are a UI concern.
 */

export type Tone = "sky" | "emerald" | "rose" | "amber" | "slate" | "violet";

export interface FeedActor {
  id: string | null;
  name: string | null;
  role: string | null;
  avatar_url?: string | null;
  online: boolean;
}

export interface FeedItem {
  id: string;
  kind: string;
  at: string;
  count: number;
  actor: FeedActor | null;
  /** False ⇒ the actor is inferred (e.g. a deal's owner), not known to have
   *  made the change. Never phrase these as "X did Y". */
  attributed: boolean;
  entity_id: string | null;
  title: string | null;
  detail: Record<string, unknown>;
}

export interface FeedResponse {
  items: FeedItem[];
  has_more: boolean;
  limit: number;
  offset: number;
  label: string;
  kinds: string[];
}

/** Semantic tones, matching the palette already used across the app:
 *  emerald = good/active, sky = routine write, rose = destructive/alert,
 *  amber = attention, violet = external (client-side) signal. */
const TONES: Record<string, Tone> = {
  "placement.published": "emerald",
  "placement.removed": "amber",
  "publish.failed": "rose",
  "deal.created": "sky",
  "deal.stage": "sky",
  "deal.commented": "slate",
  "deal.deleted": "rose",
  "client.created": "sky",
  "client.deleted": "rose",
  "share.created": "sky",
  "share.opened": "violet",
  "share.revoked": "amber",
};

export function toneOf(kind: string): Tone {
  if (TONES[kind]) return TONES[kind];
  if (kind.endsWith("_deleted") || kind.endsWith(".deleted")) return "rose";
  if (kind.startsWith("expense.")) return "slate";
  return "slate";
}

export function toneClass(tone: Tone): string {
  switch (tone) {
    case "emerald":
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    case "sky":
      return "bg-sky-500/15 text-sky-300 border-sky-500/30";
    case "rose":
      return "bg-rose-500/15 text-rose-300 border-rose-500/30";
    case "amber":
      return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    case "violet":
      return "bg-violet-500/15 text-violet-300 border-violet-500/30";
    default:
      return "bg-white/5 text-slate-400 border-white/10";
  }
}

/** Short chip label. */
export const KIND_LABELS: Record<string, string> = {
  "placement.published": "Published",
  "placement.removed": "Removed",
  "publish.failed": "Failed",
  "deal.created": "New deal",
  "deal.stage": "Deal stage",
  "deal.commented": "Comment",
  "deal.deleted": "Deal deleted",
  "client.created": "New client",
  "client.deleted": "Client deleted",
  "share.created": "Share sent",
  "share.opened": "Share opened",
  "share.revoked": "Share revoked",
  "expense.expense_created": "Expense added",
  "expense.expense_updated": "Expense edited",
  "expense.expense_deleted": "Expense deleted",
  "expense.recurring_created": "Recurring added",
  "expense.recurring_updated": "Recurring edited",
  "expense.recurring_deleted": "Recurring removed",
  "expense.recurring_auto_posted": "Recurring posted",
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

const STAGE_LABELS: Record<string, string> = {
  planning: "Planning",
  booked: "Booked",
  decision_pending: "Decision pending",
  done: "Won",
  lost: "Lost",
};

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The verb phrase — what happened, without the actor's name. The row renders
 *  the actor separately so unattributed events can drop the subject entirely. */
export function summarize(item: FeedItem): string {
  const d = item.detail || {};
  switch (item.kind) {
    case "placement.published":
      return `published ${plural(item.count, "listing")}`;
    case "placement.removed":
      return `removed ${plural(item.count, "listing")}`;
    case "publish.failed":
      return `${plural(item.count, "publish job")} failed`;
    case "deal.created":
      return `created a deal`;
    case "deal.stage":
      return `moved to ${STAGE_LABELS[String(d.stage)] ?? String(d.stage)}`;
    case "deal.commented":
      return `commented on a deal`;
    case "deal.deleted":
      return `deleted a deal`;
    case "client.created":
      return `added a client`;
    case "client.deleted":
      return `deleted a client`;
    case "share.created":
      return `sent a share link`;
    case "share.revoked":
      return `revoked a share link`;
    case "share.opened": {
      const v = Number(d.views ?? 0);
      return v > 1 ? `opened the share link (${v} views)` : `opened the share link`;
    }
    default:
      if (item.kind.startsWith("expense.")) {
        return kindLabel(item.kind).toLowerCase();
      }
      return item.kind;
  }
}

/** Muted trailing context — platform split, price, reason. */
export function subtitleOf(item: FeedItem): string | null {
  const d = item.detail || {};
  if (item.kind === "placement.published") {
    const parts: string[] = [];
    if (Number(d.ss)) parts.push(`SS ${d.ss}`);
    if (Number(d.myhome)) parts.push(`MyHome ${d.myhome}`);
    return parts.length ? parts.join(" · ") : null;
  }
  if (item.kind === "deal.stage" && d.stage === "lost" && d.lost_reason) {
    return String(d.lost_reason);
  }
  if (item.kind === "deal.stage" && d.price) {
    return `${d.price} ${d.currency ?? ""}`.trim();
  }
  if (item.kind === "publish.failed" && d.error) {
    return String(d.error);
  }
  if (item.kind === "deal.commented" && d.excerpt) {
    return String(d.excerpt);
  }
  return null;
}

/** Deep link to the entity, when one exists. */
export function hrefOf(item: FeedItem): string | null {
  if (!item.entity_id) return null;
  if (item.kind.startsWith("deal.") && item.kind !== "deal.deleted")
    return `/deals`;
  if (item.kind === "client.created") return `/clients/${item.entity_id}`;
  return null;
}
